// OrderStatusWebSocketInitializer.tsx
import React, { useContext, useEffect, useRef, useState } from 'react';
import { FeedActionContext } from './FeedState';
import { AuthenticationInfoSelector, useStateSelector } from '../global/dataSelectors';
import { useDispatcher } from '../global/hooks';
import { createSelector } from 'reselect';
import { DataState } from '../global/state';
import { useOrderStatusFeedStatus, useOrderStatusWebSocketCloseReason } from './FeedSelectors';
import { Websockets } from '../../constants';
import _ from 'lodash';
import { orderStatusWebSocketService } from './OrderStatusWebSocketService';
import { useFeedActions } from './useFeedActions';

const canConnectToWebsocket = createSelector(
  (s: DataState) => s.auth,
  auth => (auth.isExpired !== true && auth.isAuthenticated === true ? true : false)
);

const OrderStatusWebSocketInitializer: React.FC = () => {
  const dispatch = useContext(FeedActionContext);
  const [shouldReconnect, setShouldReconnect] = useState(false);
  const retryTimeoutMs = useRef<number>(250);
  const retryTimeout = useRef<NodeJS.Timeout>();
  const dispatchers = useDispatcher();
  const dispatcher = dispatchers.dispatcher.orderStatusFeed;
  const globalDispatch = dispatchers.dispatch;
  const orderStatusFeedStatus = useOrderStatusFeedStatus();
  const closeReason = useOrderStatusWebSocketCloseReason();
  const canConnect = useStateSelector(canConnectToWebsocket);
  const apiToken = useStateSelector(state => state.auth.apiToken);
  const selectedAccountId = useStateSelector(
    s => s.account.selectedLinkedAccount && s.account.selectedLinkedAccount.accountId
  );
  const { subscribeOrderUpdates, unsubscribeOrderUpdates } = useFeedActions(apiToken);
  const { accountId } = useStateSelector(AuthenticationInfoSelector);

  useEffect(() => {
    return () => {
      // Clean up WebSocket on unmount if needed
      unsubscribeOrderUpdates(accountId);
      if (selectedAccountId) unsubscribeOrderUpdates(selectedAccountId);
      orderStatusWebSocketService.closeConnection();
    };
  }, []);

  useEffect(() => {
    if (orderStatusFeedStatus && orderStatusFeedStatus === 'open') {
      subscribeOrderUpdates(accountId);
    }
  }, [orderStatusFeedStatus]);

  useEffect(() => {
    if (orderStatusFeedStatus && orderStatusFeedStatus === 'open') {
      if (selectedAccountId) {
        console.log('selected linked account', selectedAccountId);
        subscribeOrderUpdates(selectedAccountId);
      }
    }
  }, [selectedAccountId]);

  useEffect(() => {
    if (shouldReconnect && apiToken) {
      if (retryTimeout.current) {
        clearTimeout(retryTimeout.current);
      }
      retryTimeout.current = setTimeout(() => {
        if (orderStatusWebSocketService.shouldTryReconnect()) {
          orderStatusWebSocketService
            .connect(dispatch, dispatcher, globalDispatch, apiToken)
            .then(() => setShouldReconnect(false));
        }
      }, retryTimeoutMs.current);
    }
  }, [shouldReconnect, apiToken]);

  useEffect(() => {
    if (orderStatusFeedStatus === 'closed') {
      if (canConnect) {
        const newTimeout = Math.min(Websockets.ReconnectInterval, retryTimeoutMs.current + retryTimeoutMs.current);
        const newTimeoutDisplay = _.round(newTimeout / 1000, 2);
        const closeMessage = `Order Status Socket is closed. Reconnect will be attempted in ${newTimeoutDisplay} second.`;
        dispatcher.closed({
          reason: closeReason || '',
          retryingInSeconds: newTimeoutDisplay,
          message: closeMessage,
        });
        retryTimeoutMs.current = newTimeout;
        console.warn(closeMessage);
        setShouldReconnect(true);
      } else {
        const closeMessage = 'Order Status Socket is closed because user can no longer connect';
        dispatcher.closed({ reason: closeReason || '', message: closeMessage });
        console.warn(closeMessage);
      }
    }
  }, [orderStatusFeedStatus, closeReason, canConnect]);

  useEffect(() => {
    if (canConnect && apiToken) {
      console.log('initializing order status websocket (this should only happen once!)');
      orderStatusWebSocketService.connect(dispatch, dispatcher, globalDispatch, apiToken);
    } else if (!canConnect && !orderStatusWebSocketService.shouldTryReconnect()) {
      console.log('disconnecting order status websocket because user can no longer connect');
      orderStatusWebSocketService.closeConnection();
    } else {
      console.log('doing nothing');
    }
  }, [dispatcher, canConnect, apiToken]);

  return null; // This component does not render anything
};

export default OrderStatusWebSocketInitializer;
